import { useQuery } from '@tanstack/react-query';
import { Bell, ChevronRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getNotifications } from '@/app/services/notificationService';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileEmptyState } from '@/components/ui/MobileEmptyState';
import { NotificationSkeleton } from '@/components/ui/MobileSkeleton';
import { cn } from '@/lib/utils';

export default function NotificationsPage() {
    const { data, isLoading, refetch, isFetching } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => getNotifications(100, 0), // Fetch more to account for filtering
        refetchInterval: 30000,
    });

    // Filter to only show web_push notifications (backend doesn't support channel filter)
    const notifications = (data?.notifications || []).filter(
        (n) => n.channel === 'web_push'
    );

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader
                title="OTP Requests"
                showBack
                backPath="/profile"
                rightAction={
                    <button
                        onClick={() => refetch()}
                        className="flex items-center justify-center w-10 h-10 rounded-full active:bg-accent"
                        disabled={isFetching}
                    >
                        <RefreshCw
                            className={cn(
                                "h-5 w-5 text-muted-foreground",
                                isFetching && "animate-spin"
                            )}
                        />
                    </button>
                }
            />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                {isLoading ? (
                    <NotificationSkeleton />
                ) : notifications.length === 0 ? (
                    <MobileEmptyState
                        icon={<Bell className="h-16 w-16" />}
                        title="No OTP Requests"
                        description="You don't have any pending OTP approval requests"
                    />
                ) : (
                    <div className="p-4 space-y-3">
                        {notifications.map((notification) => (
                            <Link
                                key={notification.id}
                                to={`/notifications/${notification.id}`}
                                className="block"
                            >
                                <div className="mobile-card active:bg-accent transition-colors">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            {/* Title and badge row */}
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <span className="font-semibold text-foreground truncate">
                                                    {notification.title}
                                                </span>
                                                {notification.status === 'pending' && (
                                                    <MobileBadge variant="default">
                                                        New
                                                    </MobileBadge>
                                                )}
                                                {notification.status === 'sent' && !notification.read_at && (
                                                    <MobileBadge variant="warning">
                                                        Unread
                                                    </MobileBadge>
                                                )}
                                            </div>

                                            {/* Body */}
                                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                                {notification.body}
                                            </p>

                                            {/* Meta info */}
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                {notification.data?.party_name && (
                                                    <span className="truncate max-w-[120px]">
                                                        {notification.data.party_name}
                                                    </span>
                                                )}
                                                {notification.data?.invoice_amount && (
                                                    <span className="font-medium text-foreground">
                                                        ₹{notification.data.invoice_amount.toLocaleString()}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Time */}
                                            <p className="text-xs text-muted-foreground mt-2">
                                                {formatDistanceToNow(new Date(notification.created_at), {
                                                    addSuffix: true
                                                })}
                                            </p>
                                        </div>

                                        {/* Arrow */}
                                        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
